import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ToastProvider } from '@whinerp/ui';
import { OfflineQueueProvider } from '../offline/queue.js';
import { I18nProvider } from './i18n.js';
import { ThemeProvider } from './theme.js';

/**
 * Everything a screen needs above it.
 *
 * The same tree wraps the running application and every UI test, so a test
 * cannot pass because it rendered a screen in an environment the product never
 * provides. Authenticated screens get a SessionProvider inside this, from the
 * shell - unauthenticated ones (signup, sign-in) do not have a session yet and
 * must not be written as though they do.
 */

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // A failed read shows an error state the person can see and retry from,
        // rather than retrying invisibly and looking slow.
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: { retry: false },
    },
  });
}

export interface ProvidersProps {
  children: ReactNode;
  queryClient?: QueryClient;
  language?: 'en' | 'ar';
  locale?: string;
  timeZone?: string;
  currency?: string;
  numerals?: 'latn' | 'arab';
  theme?: 'light' | 'dark' | 'system';
}

export function Providers({
  children,
  queryClient,
  language,
  locale,
  timeZone,
  currency,
  numerals,
  theme,
}: ProvidersProps): JSX.Element {
  const client = queryClient ?? createQueryClient();
  return (
    <QueryClientProvider client={client}>
      <ThemeProvider choice={theme ?? 'system'}>
        <I18nProvider
          language={language}
          locale={locale}
          timeZone={timeZone}
          currency={currency}
          numerals={numerals}
        >
          {/* The queue wraps everything that can write, which is everything.
              It configures the one client runtime, so a screen never has to
              know whether it is online - it asks for a save and is told
              whether the workspace has it. */}
          <OfflineQueueProvider>
            <ToastProvider>{children}</ToastProvider>
          </OfflineQueueProvider>
        </I18nProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
